import React, { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import MoodSelector from './components/MoodSelector';
import ActivitySelector from './components/ActivitySelector';
import GenreSelector from './components/GenreSelector';
import CustomRules from './components/CustomRules';
import PlaylistView from './components/PlaylistView';
import MusicPlayer from './components/MusicPlayer';
import ThemePlaylists from './components/ThemePlaylists';
import PlaylistLibrary from './components/PlaylistLibrary';
import MusicDiscovery from './components/MusicDiscovery';
import SocialShare from './components/SocialShare';
import songs from './data/songs';
import './App.css';

function App() {
  const [activePage, setActivePage] = useState('generator');
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [customRules, setCustomRules] = useState({
    tempo: null,
    energy: null,
    danceability: null,
    acousticness: null,
    vocalStyle: null,
  });
  const [playlist, setPlaylist] = useState([]);
  const [currentSong, setCurrentSong] = useState(null);
  const [activeTheme, setActiveTheme] = useState(null);
  const [savedPlaylists, setSavedPlaylists] = useState(() => {
    try {
      const stored = localStorage.getItem('musicPlaylists');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [playlistName, setPlaylistName] = useState('');

  const generatePlaylist = useCallback(() => {
    let filtered = [...songs];

    if (selectedMoods.length > 0) {
      filtered = filtered.filter(s =>
        s.mood.some(m => selectedMoods.includes(m))
      );
    }

    if (selectedActivities.length > 0) {
      filtered = filtered.filter(s =>
        s.activities.some(a => selectedActivities.includes(a))
      );
    }

    if (selectedGenres.length > 0) {
      filtered = filtered.filter(s =>
        s.genres.some(g => selectedGenres.includes(g))
      );
    }

    if (customRules.tempo) {
      filtered = filtered.filter(s => s.tempo === customRules.tempo);
    }
    if (customRules.energy) {
      filtered = filtered.filter(s => s.energy === customRules.energy);
    }
    if (customRules.danceability) {
      filtered = filtered.filter(s => s.danceability === customRules.danceability);
    }
    if (customRules.acousticness) {
      filtered = filtered.filter(s => s.acousticness === customRules.acousticness);
    }
    if (customRules.vocalStyle) {
      filtered = filtered.filter(s => s.vocalStyle === customRules.vocalStyle);
    }

    // Shuffle
    for (let i = filtered.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
    }

    setPlaylist(filtered);
  }, [selectedMoods, selectedActivities, selectedGenres, customRules]);

  const handleThemeSelect = (theme) => {
    if (activeTheme === theme.id) {
      setActiveTheme(null);
      setSelectedMoods([]);
      setSelectedActivities([]);
      setSelectedGenres([]);
      setCustomRules({ tempo: null, energy: null, danceability: null, acousticness: null, vocalStyle: null });
      return;
    }
    setActiveTheme(theme.id);
    setSelectedMoods(theme.moods);
    setSelectedActivities(theme.activities);
    setSelectedGenres(theme.genres);
  };

  const playSong = (song) => {
    setCurrentSong(song);
  };

  const playNext = useCallback(() => {
    if (!currentSong || playlist.length === 0) return;
    const idx = playlist.findIndex(s => s.id === currentSong.id);
    const next = (idx + 1) % playlist.length;
    setCurrentSong(playlist[next]);
  }, [currentSong, playlist]);

  const playPrev = useCallback(() => {
    if (!currentSong || playlist.length === 0) return;
    const idx = playlist.findIndex(s => s.id === currentSong.id);
    const prev = (idx - 1 + playlist.length) % playlist.length;
    setCurrentSong(playlist[prev]);
  }, [currentSong, playlist]);

  const removeSong = (songId) => {
    setPlaylist(prev => prev.filter(s => s.id !== songId));
  };

  const savePlaylist = () => {
    const tags = [
      ...selectedMoods.map(m => m),
      ...selectedActivities.map(a => a),
      ...selectedGenres.map(g => g),
    ];
    const category = selectedMoods.length > 0 ? 'mood'
      : selectedActivities.length > 0 ? 'activity'
      : selectedGenres.length > 0 ? 'genre' : 'custom';

    const newPlaylist = {
      id: uuidv4(),
      name: playlistName || '未命名播放列表',
      songs: playlist,
      tags,
      category,
      createdAt: new Date().toLocaleDateString('zh-CN'),
    };

    const updated = [...savedPlaylists, newPlaylist];
    setSavedPlaylists(updated);
    localStorage.setItem('musicPlaylists', JSON.stringify(updated));
    setShowSaveModal(false);
    setPlaylistName('');
  };

  const loadPlaylist = (pl) => {
    setPlaylist(pl.songs);
    setActivePage('generator');
  };

  const deletePlaylist = (id) => {
    const updated = savedPlaylists.filter(p => p.id !== id);
    setSavedPlaylists(updated);
    localStorage.setItem('musicPlaylists', JSON.stringify(updated));
  };

  const clearSelections = () => {
    setSelectedMoods([]);
    setSelectedActivities([]);
    setSelectedGenres([]);
    setCustomRules({ tempo: null, energy: null, danceability: null, acousticness: null, vocalStyle: null });
    setActiveTheme(null);
    setPlaylist([]);
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>🎶 音乐播放列表生成器</h1>
        <p>根据你的心情、活动和偏好创建个性化播放列表</p>
      </header>

      <nav className="app-nav">
        <button
          className={`nav-btn ${activePage === 'generator' ? 'active' : ''}`}
          onClick={() => setActivePage('generator')}
        >
          🎵 播放列表生成
        </button>
        <button
          className={`nav-btn ${activePage === 'discovery' ? 'active' : ''}`}
          onClick={() => setActivePage('discovery')}
        >
          🔍 音乐发现
        </button>
        <button
          className={`nav-btn ${activePage === 'library' ? 'active' : ''}`}
          onClick={() => setActivePage('library')}
        >
          📚 我的播放列表
        </button>
      </nav>

      {activePage === 'generator' && (
        <div className="generator-section">
          <div className="generator-controls">
            <MoodSelector selectedMoods={selectedMoods} onMoodChange={setSelectedMoods} />
            <ActivitySelector selectedActivities={selectedActivities} onActivityChange={setSelectedActivities} />
            <GenreSelector selectedGenres={selectedGenres} onGenreChange={setSelectedGenres} />
            <CustomRules rules={customRules} onRulesChange={setCustomRules} />
            <ThemePlaylists onSelectTheme={handleThemeSelect} activeTheme={activeTheme} />
            <button className="generate-btn" onClick={generatePlaylist}>
              🎲 生成播放列表
            </button>
            <button className="generate-btn" onClick={clearSelections} style={{
              background: 'transparent',
              border: '2px solid var(--accent-color)',
              color: 'var(--accent-color)',
              marginBottom: 0,
            }}>
              🔄 重置选择
            </button>
          </div>
          <div className="generator-result">
            <PlaylistView
              playlist={playlist}
              onPlay={playSong}
              onRemoveSong={removeSong}
              currentSong={currentSong}
              onSavePlaylist={() => setShowSaveModal(true)}
            />
            {playlist.length > 0 && <SocialShare playlist={playlist} />}
          </div>
        </div>
      )}

      {activePage === 'discovery' && (
        <div className="discovery-page">
          <MusicDiscovery onPlaySong={playSong} />
        </div>
      )}

      {activePage === 'library' && (
        <div className="library-page">
          <PlaylistLibrary
            savedPlaylists={savedPlaylists}
            onLoadPlaylist={loadPlaylist}
            onDeletePlaylist={deletePlaylist}
          />
        </div>
      )}

      {currentSong && (
        <MusicPlayer
          song={currentSong}
          playlist={playlist}
          onNext={playNext}
          onPrev={playPrev}
          onClose={() => setCurrentSong(null)}
        />
      )}

      {showSaveModal && (
        <div className="save-modal-overlay" onClick={() => setShowSaveModal(false)}>
          <div className="save-modal" onClick={e => e.stopPropagation()}>
            <h3>💾 保存播放列表</h3>
            <input
              type="text"
              placeholder="输入播放列表名称..."
              value={playlistName}
              onChange={e => setPlaylistName(e.target.value)}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && savePlaylist()}
            />
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setShowSaveModal(false)}>
                取消
              </button>
              <button className="modal-save" onClick={savePlaylist}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
